/**
 * GroceryTab — Grocery list with inline store search links.
 *
 * Each ingredient row: checkbox → name → quantity → recipe → store buttons (up to 3)
 *
 * Loads real grocery data from GET /v1/households/{hhId}/grocery-list/{weekId}.
 * Store search URLs are generated client-side using the household's configured
 * groceryStoreIds + SUPPORTED_STORES templates + buildStoreSearchUrl() from shared.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  RefreshCw, Search as SearchIcon, Clipboard,
  Share2, Settings, Loader, ShoppingCart, CheckCircle, StickyNote,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SUPPORTED_STORES, buildStoreSearchUrl, getWeekId, formatWeekRange } from '@tabletryb/shared';
import type { GroceryStore } from '@tabletryb/shared';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../context/AuthContext';
import OnboardingTooltip from '../common/OnboardingTooltip';

// ── Short label for store link buttons ────────────────────────────────────
const STORE_SHORT_LABELS: Record<string, string> = {
  'kroger': 'Kroger', 'kroger-ralphs': 'Ralphs',
  'kroger-harris-teeter': 'Harris Teeter', 'kroger-fred-meyer': 'Fred Meyer',
  'heb': 'H‑E‑B', 'walmart': 'Walmart', 'target': 'Target',
  'costco': 'Costco', 'whole-foods': 'Whole Foods', 'publix': 'Publix',
  'aldi': 'Aldi', 'trader-joes': "Trader Joe's",
  'safeway': 'Safeway', 'albertsons': "Albertsons",
};

// ── Aisle emoji mapping ────────────────────────────────────────────────────
const AISLE_EMOJI: Record<string, string> = {
  'Produce': '🥬', 'Meat & Seafood': '🥩', 'Dairy & Eggs': '🥚',
  'Bakery & Bread': '🍞', 'Frozen': '🧊', 'Canned & Jarred': '🥫',
  'Dry Goods & Pasta': '🍝', 'Pasta & Grains': '🍝',
  'Condiments & Sauces': '🫙', 'Sauces & Condiments': '🫙',
  'Spices & Seasonings': '🧂', 'Oils & Vinegars': '🫒',
  'Beverages': '🥤', 'Snacks': '🍿', 'Baking': '🧁',
  'Deli': '🧀', 'International': '🌍', 'Other': '📦',
};

// ── Aisle sort order ───────────────────────────────────────────────────────
const AISLE_ORDER = [
  'Produce', 'Meat & Seafood', 'Dairy & Eggs', 'Deli', 'Bakery & Bread',
  'Frozen', 'Canned & Jarred', 'Pasta & Grains', 'Dry Goods & Pasta',
  'Sauces & Condiments', 'Condiments & Sauces', 'Oils & Vinegars',
  'International', 'Spices & Seasonings', 'Beverages', 'Snacks', 'Baking', 'Other',
];

// ── Types ──────────────────────────────────────────────────────────────────
interface GroceryItemData {
  name: string;
  amount: number;
  unit: string;
  aisle: string;
  recipes: string[];
  isPantryStaple?: boolean;
}

interface GroceryListResponse {
  weekId: string;
  recipes: string[];
  recipeCount: number;
  shoppingList: GroceryItemData[];
  pantryCheck: GroceryItemData[];
  shoppingItemCount: number;
  pantryItemCount: number;
  appleNotesText?: string;
  message?: string;
}

interface ResolvedStore {
  storeId: string;
  label: string;
  store: GroceryStore;
}

// ── Helpers ────────────────────────────────────────────────────────────────
function formatAmount(amount: number): string {
  if (!amount) return '';
  if (amount === Math.floor(amount)) return String(Math.floor(amount));
  const frac = amount - Math.floor(amount);
  const whole = Math.floor(amount);
  const prefix = whole > 0 ? `${whole} ` : '';
  if (Math.abs(frac - 0.25) < 0.06) return `${prefix}1/4`.trim();
  if (Math.abs(frac - 0.33) < 0.06) return `${prefix}1/3`.trim();
  if (Math.abs(frac - 0.5) < 0.06) return `${prefix}1/2`.trim();
  if (Math.abs(frac - 0.67) < 0.06) return `${prefix}2/3`.trim();
  if (Math.abs(frac - 0.75) < 0.06) return `${prefix}3/4`.trim();
  return amount.toFixed(1);
}

function pluralUnit(unit: string, amount: number): string {
  if (!unit) return '';
  const pluralisable = ['cup', 'tbsp', 'tsp', 'lb', 'clove', 'slice', 'piece', 'can', 'sprig', 'stalk', 'head', 'ear'];
  if (amount > 1 && pluralisable.includes(unit)) return unit + 's';
  return unit;
}

function groupByAisle(items: GroceryItemData[]): Record<string, GroceryItemData[]> {
  const groups: Record<string, GroceryItemData[]> = {};
  for (const item of items) {
    const aisle = item.aisle || 'Other';
    if (!groups[aisle]) groups[aisle] = [];
    groups[aisle].push(item);
  }
  return groups;
}

function sortAisles(groups: Record<string, GroceryItemData[]>): string[] {
  const keys = Object.keys(groups);
  return keys.sort((a, b) => {
    const ai = AISLE_ORDER.indexOf(a);
    const bi = AISLE_ORDER.indexOf(b);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });
}

// ── Component ──────────────────────────────────────────────────────────────
const GroceryTab: React.FC = () => {
  const api = useApi();
  const { user } = useAuth();
  const navigate = useNavigate();
  const householdId = user?.householdId;
  const weekId = getWeekId();

  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [groceryStoreIds, setGroceryStoreIds] = useState<string[]>([]);
  const [groceryData, setGroceryData] = useState<GroceryListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copyToast, setCopyToast] = useState(false);

  // Load grocery list + settings
  const loadData = useCallback(async () => {
    if (!householdId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const [settingsData, groceryResp] = await Promise.all([
        api.get<{ settings?: { groceryStoreIds?: string[] } }>(`/v1/households/${householdId}`),
        api.get<GroceryListResponse>(`/v1/households/${householdId}/grocery-list/${weekId}`),
      ]);
      setGroceryStoreIds(settingsData.settings?.groceryStoreIds ?? []);
      setGroceryData(groceryResp);
    } catch (err: unknown) {
      console.error('Failed to load grocery list:', err);
      setError('Failed to load grocery list. Make sure you have finalized meals for this week.');
    } finally {
      setLoading(false);
    }
  }, [householdId, weekId]); // eslint-disable-line

  useEffect(() => { loadData(); }, [loadData]);

  // Derive data from API response
  const shoppingItems = groceryData?.shoppingList ?? [];
  const pantryItems = groceryData?.pantryCheck ?? [];
  const recipeNames = groceryData?.recipes ?? [];

  // Resolve store IDs to full store objects
  const resolvedStores: ResolvedStore[] = useMemo(() => {
    return groceryStoreIds
      .map((id) => {
        const store = SUPPORTED_STORES.find((s) => s.storeId === id);
        if (!store) return null;
        return { storeId: id, label: STORE_SHORT_LABELS[id] || store.displayName, store };
      })
      .filter(Boolean) as ResolvedStore[];
  }, [groceryStoreIds]);

  const toggleCheck = (key: string) => {
    setChecked((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const checkedCount = Object.values(checked).filter(Boolean).length;
  const totalItems = shoppingItems.length + pantryItems.length;

  const shoppingByAisle = groupByAisle(shoppingItems);
  const pantryByAisle = groupByAisle(pantryItems);
  const sortedAisles = sortAisles(shoppingByAisle);
  const sortedPantryAisles = sortAisles(pantryByAisle);

  const copyList = async () => {
    // Use appleNotesText from backend if available, else build locally
    const text = groceryData?.appleNotesText || (() => {
      const lines: string[] = ['🛒 Grocery List', ''];
      for (const aisle of sortedAisles) {
        lines.push(`📍 ${aisle}`);
        for (const item of shoppingByAisle[aisle].sort((a, b) => a.name.localeCompare(b.name))) {
          const qty = formatAmount(item.amount);
          const unit = pluralUnit(item.unit, item.amount);
          const parts = [qty, unit, item.name.charAt(0).toUpperCase() + item.name.slice(1)].filter(Boolean);
          lines.push(`- ${parts.join(' ')}`);
        }
        lines.push('');
      }
      return lines.join('\n');
    })();
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    // Show confirmation toast
    setCopyToast(true);
    setTimeout(() => setCopyToast(false), 5000);
  };

  /** Share to Notes/Keep — uses native share sheet or copies with guidance */
  const shareToNotes = async () => {
    const text = groceryData?.appleNotesText || '';
    if (!text) return;
    if (typeof navigator !== 'undefined' && 'share' in navigator) {
      try {
        await navigator.share({ title: 'Grocery List', text });
      } catch { /* user cancelled share */ }
    } else {
      // Desktop fallback — copy and show guidance
      await copyList();
    }
  };

  if (loading) {
    return (
      <div className="tab-panel">
        <div className="loading-container">
          <Loader size={24} className="spin" />
          <p>Loading grocery list...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tab-panel">
        <div className="empty-state">
          <ShoppingCart size={48} />
          <h3>Couldn't load grocery list</h3>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={loadData} style={{ marginTop: 12 }}>
            <RefreshCw size={16} /> Try Again
          </button>
        </div>
      </div>
    );
  }

  if (totalItems === 0) {
    return (
      <div className="tab-panel">
        <div className="empty-state">
          <ShoppingCart size={48} />
          <h3>No grocery list yet</h3>
          <p>Finalize your meal selections on the Results tab first, then your grocery list will appear here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tab-panel">
      {/* Header */}
      <div className="tab-header">
        <div>
          <h2>Grocery List</h2>
          <p className="tab-subtitle">
            {formatWeekRange(weekId)} — {groceryData?.recipeCount ?? 0} meals, {shoppingItems.length} items to buy
          </p>
        </div>
        <div className="tab-header-action">
          <button className="btn btn-secondary" onClick={copyList}>
            <Clipboard size={16} />
            Copy List
          </button>
          <button className="btn btn-secondary" onClick={shareToNotes}>
            <StickyNote size={16} />
            Send to Notes
          </button>
          <button className="btn btn-secondary" onClick={loadData}>
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Copy confirmation toast */}
      {copyToast && (
        <div
          className="info-banner info-banner--success"
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
          onClick={() => setCopyToast(false)}
        >
          <CheckCircle size={16} />
          <span>List copied! Paste into <strong>Apple Notes</strong> or <strong>Google Keep</strong> — items become checkboxes automatically.</span>
        </div>
      )}

      {/* First-time tooltip */}
      <OnboardingTooltip
        id="grocery-export"
        message="'Copy List' copies to your clipboard. 'Send to Notes' opens your phone's share sheet to send directly to Apple Notes, Google Keep, Messages, or any app. Items become checkboxes you can check off while shopping!"
      />

      {/* Store config prompt */}
      {resolvedStores.length === 0 && (
        <div className="grocery-store-prompt">
          <Settings size={18} />
          <span>
            No stores configured.{' '}
            <button className="grocery-store-prompt-link" onClick={() => navigate('/app/settings')}>
              Select your stores in Settings
            </button>{' '}
            to see search links on each item.
          </span>
        </div>
      )}

      {/* Store legend (when stores are configured) */}
      {resolvedStores.length > 0 && (
        <div className="grocery-store-legend">
          <span className="grocery-store-legend-label">Search links:</span>
          {resolvedStores.map((rs, i) => (
            <span key={rs.storeId} className={`grocery-store-legend-chip grocery-store-legend-chip--${i}`}>
              <SearchIcon size={11} />
              {rs.label}
            </span>
          ))}
        </div>
      )}

      {resolvedStores.length > 0 && (
        <OnboardingTooltip
          id="grocery-store-links"
          message="For best results, open your store's website in another tab and log in first. Then tap a store button on any item — it opens that store with the ingredient searched, and items you add drop into your cart automatically (no matter how many tabs are open)."
          icon="🔍"
        />
      )}

      {/* Meals included */}
      <div className="grocery-meals-bar">
        <span className="grocery-meals-bar-label">Meals on this list:</span>
        <div className="grocery-meal-chips">
          {recipeNames.map((name, i) => (
            <span key={i} className="grocery-meal-chip">{name}</span>
          ))}
        </div>
      </div>

      {/* Progress */}
      <div className="grocery-progress">
        <div className="grocery-progress-bar">
          <div
            className="grocery-progress-fill"
            style={{ width: `${totalItems ? (checkedCount / totalItems) * 100 : 0}%` }}
          />
        </div>
        <span className="grocery-progress-text">
          {checkedCount} of {totalItems} items checked
        </span>
      </div>

      {/* Column headers */}
      {shoppingItems.length > 0 && (
        <div className="grocery-col-headers">
          <span className="grocery-col-check" />
          <span className="grocery-col-name">Item</span>
          <span className="grocery-col-qty">Qty</span>
          <span className="grocery-col-recipe">Recipe</span>
          {resolvedStores.length > 0 && (
            <span className="grocery-col-store">Search</span>
          )}
        </div>
      )}

      {/* Shopping list by aisle */}
      <div className="grocery-section-v2">
        {sortedAisles.map((aisle) => (
          <div key={aisle} className="grocery-aisle-v2">
            <div className="grocery-aisle-header-v2">
              <span className="grocery-aisle-emoji">{AISLE_EMOJI[aisle] || '📦'}</span>
              <span className="grocery-aisle-name-v2">{aisle}</span>
              <span className="grocery-aisle-count-v2">{shoppingByAisle[aisle].length}</span>
            </div>
            <div className="grocery-items-v2">
              {shoppingByAisle[aisle]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((item, i) => {
                  const key = `shop-${aisle}-${i}`;
                  return (
                    <GroceryItemRow
                      key={key}
                      item={item}
                      isChecked={!!checked[key]}
                      onToggle={() => toggleCheck(key)}
                      stores={resolvedStores}
                    />
                  );
                })}
            </div>
          </div>
        ))}
      </div>

      {/* Pantry check */}
      {pantryItems.length > 0 && (
        <div className="grocery-pantry-v2">
          <h3 className="grocery-pantry-title">🏠 Check Your Pantry</h3>
          <p className="grocery-pantry-subtitle">
            You probably have these on hand — verify before you shop.
          </p>
          {sortedPantryAisles.map((aisle) => (
            <div key={aisle} className="grocery-aisle-v2">
              <div className="grocery-aisle-header-v2 pantry">
                <span className="grocery-aisle-emoji">{AISLE_EMOJI[aisle] || '📦'}</span>
                <span className="grocery-aisle-name-v2">{aisle}</span>
                <span className="grocery-aisle-count-v2">{pantryByAisle[aisle].length}</span>
              </div>
              <div className="grocery-items-v2">
                {pantryByAisle[aisle]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((item, i) => {
                    const key = `pantry-${aisle}-${i}`;
                    return (
                      <GroceryItemRow
                        key={key}
                        item={item}
                        isChecked={!!checked[key]}
                        onToggle={() => toggleCheck(key)}
                        stores={resolvedStores}
                        isPantry
                      />
                    );
                  })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Grocery Item Row ───────────────────────────────────────────────────────
interface GroceryItemRowProps {
  item: GroceryItemData;
  isChecked: boolean;
  onToggle: () => void;
  stores: ResolvedStore[];
  isPantry?: boolean;
}

const GroceryItemRow: React.FC<GroceryItemRowProps> = ({
  item, isChecked, onToggle, stores, isPantry,
}) => {
  const qty = formatAmount(item.amount);
  const unit = pluralUnit(item.unit, item.amount);
  const qtyStr = [qty, unit].filter(Boolean).join(' ');
  const displayName = item.name.charAt(0).toUpperCase() + item.name.slice(1);

  // Recipe display: show first recipe name, or "N recipes" if multiple
  const recipeLabel = item.recipes.length === 0
    ? ''
    : item.recipes.length === 1
      ? item.recipes[0]
      : `${item.recipes.length} recipes`;
  const recipeTooltip = item.recipes.join(', ');

  return (
    <div className={`grocery-row ${isChecked ? 'grocery-row--checked' : ''} ${isPantry ? 'grocery-row--pantry' : ''}`}>
      {/* Checkbox */}
      <button
        className={`grocery-row-check ${isChecked ? 'grocery-row-check--on' : ''}`}
        onClick={onToggle}
        aria-label={`Mark ${displayName} as ${isChecked ? 'unchecked' : 'checked'}`}
      >
        {isChecked && '✓'}
      </button>

      {/* Item name */}
      <span className={`grocery-row-name ${isChecked ? 'grocery-row-name--struck' : ''}`}>
        {displayName}
      </span>

      {/* Quantity */}
      <span className="grocery-row-qty">{qtyStr}</span>

      {/* Recipe */}
      {recipeLabel && (
        <span className="grocery-row-recipe" title={recipeTooltip}>
          {recipeLabel}
        </span>
      )}

      {/* Store search buttons */}
      {stores.length > 0 && (
        <span className="grocery-row-stores">
          {stores.map((rs, i) => {
            const url = rs.store.searchUrlTemplate
              ? buildStoreSearchUrl(rs.store.searchUrlTemplate, item.name)
              : '#';
            return (
              <a
                key={rs.storeId}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className={`grocery-store-btn grocery-store-btn--${i}`}
                title={`Search ${rs.store.displayName} for ${displayName}`}
                onClick={(e) => e.stopPropagation()}
              >
                <SearchIcon size={13} />
                {rs.label}
              </a>
            );
          })}
        </span>
      )}
    </div>
  );
};

export default GroceryTab;